import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Paper, Tabs, Tab, Typography } from '@mui/material';

// Restaurant Masters workspace — WORKFLOW CONSOLIDATION ONLY.
//
// A thin horizontal tab strip over the nine existing restaurant master pages.
// Each tab simply navigates to the page's EXISTING route, so URLs, bookmarks,
// the sidebar links, permissions and every page component stay exactly as
// they are. This file adds navigation; it changes nothing else.
//
// Visibility mirrors the sidebar's rule for the same items (AdminLayout):
// master admins see everything; otherwise a tab shows only when the user's
// sessionStorage permission grants contain its menuKey. Fail-open like the
// sidebar guard — no permission list stored -> nothing is hidden.
const TABS = [
  { label: 'Department', path: '/admin/AddDepartment', menuKey: 'AllCategory' },
  { label: 'Area', path: '/admin/Createarea', menuKey: 'Createarea' },
  { label: 'Categories', path: '/admin/Category', menuKey: 'Category' },
  { label: 'Items', path: '/admin/AddItem', menuKey: 'AddItem' },
  {
    label: 'Variants',
    path: '/admin/VariantManagement',
    menuKey: 'VariantManagement',
  },
  { label: 'Store', path: '/admin/StoreMaster', menuKey: 'StoreMaster' },
  {
    label: 'Store Mapping',
    path: '/admin/ItemStoreMapping',
    menuKey: 'ItemStoreMapping',
  },
  { label: 'Area Pricing', path: '/admin/AreaPricing', menuKey: 'AreaPricing' },
  {
    label: 'Menu Upload History',
    path: '/admin/MenuUploadHistory',
    menuKey: 'AddItem',
  },
];

const allowedTabs = () => {
  let user = null;
  try {
    user = JSON.parse(sessionStorage.getItem('user'));
  } catch (_) {}
  if (user?.isMaster) return TABS;

  const raw = sessionStorage.getItem('permissions');
  if (!raw) return TABS; // fail-open, same as the sidebar guard
  try {
    const keys = new Set(JSON.parse(raw).map((p) => p.menuKey));
    return TABS.filter((t) => keys.has(t.menuKey));
  } catch (_) {
    return TABS;
  }
};

export default function RestaurantMastersLayout() {
  const location = useLocation();
  const navigate = useNavigate();

  const tabs = React.useMemo(allowedTabs, []);
  const current = location.pathname.replace(/\/+$/, '');
  const activeIdx = tabs.findIndex(
    (t) => current === t.path || current.startsWith(t.path + '/'),
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        elevation={0}
        square
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: { xs: 1, sm: 2 },
          pt: 1,
          bgcolor: 'background.paper',
        }}>
        <Typography
          variant='subtitle2'
          sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
          Restaurant Setup
        </Typography>
        <Tabs
          value={activeIdx === -1 ? false : activeIdx}
          onChange={(_, idx) => navigate(tabs[idx].path)}
          variant='scrollable'
          scrollButtons='auto'
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 600,
            },
          }}>
          {tabs.map((t) => (
            <Tab key={t.path} label={t.label} />
          ))}
        </Tabs>
      </Paper>

      {/* The existing page renders below, completely unchanged. */}
      <Outlet />
    </Box>
  );
}